/**
 * Kural tabanlı atama paneli (AKM POC §6.2) — "şu hedefe, şu tarihten itibaren, bu politika".
 *
 * Çalışma planı, izin politikası, fazla mesai politikası, uyum profili ve tatil takvimi detay
 * sayfalarında kişi başı "Ata" çekmecesinin ve atanmış kişi listesinin YERİNE geçer. Eski kişi başı
 * atamalar (UserWorkPlan / User.LeavePolicyId / Employee.HolidayCalendarId) sunucuda dönüştürülmez;
 * listede "Kişiye Özel (Eski Atama)" satırı olarak "Baştan Beri" geçerli görünür ve yalnız
 * kaldırılabilir (düzenlenemez). Eski atama her kuralı ezer (inceleme I8): eski satırı olan türlerde
 * (LEGACY_KINDS) tablonun üstünde eski atama SAYISI ve yazma yetkisiyle "Eski Atamaların Tümünü Kaldır"
 * gösterilir — kişiler kurallara / varsayılana düşer.
 *
 * Props:
 *   kind      — workPlan | leavePolicy | overtimePolicy | complianceProfile | holidayCalendar
 *   policyId  — türün politika kaydı
 *   title     — DataTable başlığı (verilmezse policyRule.title)
 *   canWrite  — türün modülünde yazma yetkisi; false iken "Yeni Kural" ve satır eylemleri yok
 *   tableBodyMaxHeight — verilirse aynen DataTable'a gider; verilmezse panel kendi yüksekliğini ölçer
 *                (tek kaydırma: yalnız tablo satırları kayar, panel kabı min-height:0 ile küçülür).
 *   targetTypes — seçilebilir hedef türleri (varsayılan TARGET_TYPE_ORDER). Onay akışı kurallarında kişi hedefi yoktur:
 *                kişiye özel atama ayrı sekmededir (spec 2026-11-01 §4.2).
 *   departmentPathLabels — departman seçenekleri ve hedef adı kökten yol ("IT › System Security", spec §4.6).
 *   impactPreview(change) — verilirse kayıt / silme ÖNCESİ "kimin akışı değişir?" önizlemesi (§4.5); change =
 *                { action: 'create' | 'update' | 'delete', ruleId?, body? }, dönüş api yanıtı (data.data =
 *                { changedCount, truncated, items }). Kimse değişmiyorsa eylem doğrudan yapılır.
 *   onChanged() — kural eklenince / değişince / silinince (kardeş sekmeler tazelensin).
 * Yeni prop'lar verilmediğinde davranış birebir öncekidir.
 */
import { useImpactPreview } from './ImpactPreviewDialog';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import policyRuleService from '../../services/policyRuleService';
import { formatDate, parseTableParams } from '../../utils/Utils';
import { ALL_EMPLOYEES_GROUP_ID } from '../../utils/assignmentConstants';
import DataTable from './DataTable';
import GenericFormDrawer from '../GenericFormDrawer';
import ConfirmDialog from './ConfirmDialog';
import OptionsMenu from './OptionsMenu';
import GenericSelect from './GenericSelect';
import AsyncGenericSelect from './AsyncGenericSelect';
import TextInput from './TextInput.jsx';
import DateTimePicker from './DateTimePicker';
import Button from './Button';
import PersonCell from './PersonCell';

// Backend PolicyRuleTargetTypes ile BİREBİR (AssignmentScopeTarget.AssignmentType hizası; 6 ünvan kural
// hedefi DEĞİLDİR).
export const TARGET = {
  COMPANY: 0, DEPARTMENT: 1, LOCATION: 2, USER_GROUP: 3, PERSON: 4, SUB_TENANT: 5, EMPLOYEE_STATUS: 7,
};

export const TARGET_TYPE_KEYS = {
  [TARGET.COMPANY]: 'company',
  [TARGET.DEPARTMENT]: 'department',
  [TARGET.LOCATION]: 'location',
  [TARGET.USER_GROUP]: 'userGroup',
  [TARGET.PERSON]: 'person',
  [TARGET.SUB_TENANT]: 'subTenant',
  [TARGET.EMPLOYEE_STATUS]: 'employeeStatus',
};

// Seçicideki sıra = öncelik sırası (kişi > grup/statü > departman > lokasyon > alt şirket > şirket).
export const TARGET_TYPE_ORDER = [
  TARGET.PERSON, TARGET.USER_GROUP, TARGET.EMPLOYEE_STATUS, TARGET.DEPARTMENT,
  TARGET.LOCATION, TARGET.SUB_TENANT, TARGET.COMPANY,
];

// Hedef türü → arama ucu. Kişi hedefi Employee.Id ile kaydedilir (sunucu kimlik ailesini UserKeys ile eşler).
export const TARGET_SEARCH_PATHS = {
  [TARGET.PERSON]: '/v1/employees',
  [TARGET.USER_GROUP]: '/v1/user-groups',
  [TARGET.EMPLOYEE_STATUS]: '/v1/employee-statuses',
  [TARGET.DEPARTMENT]: '/v1/departments',
  [TARGET.LOCATION]: '/v1/locations',
  [TARGET.SUB_TENANT]: '/v1/sub-tenants',
};

// Eski kişi başı ataması olan türler (UserWorkPlan / User.LeavePolicyId / Employee.HolidayCalendarId).
// Fazla mesai politikası ve uyum profili yalnız kurallarla atanır — eski satırları yoktur.
export const LEGACY_KINDS = Object.freeze(['workPlan', 'leavePolicy', 'holidayCalendar']);

const listItems = (res) => {
  const r = res?.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

const personName = (e) => `${e?.firstName || ''} ${e?.lastName || ''}`.trim();

export const searchTargets = async (targetType, q, { departmentPath = false } = {}) => {
  const path = TARGET_SEARCH_PATHS[targetType];
  if (!path) return [];
  if (targetType === TARGET.PERSON) {
    // Kişi seçici: lookup → hassas alanlar boş, hassas görüntüleme olayı yazılmaz (§9.1).
    const params = q ? { 'fullName[like]': q, pageSize: 20, lookup: true } : { pageSize: 20, lookup: true };
    const res = await api.get(path, { params });
    return listItems(res).map((e) => ({ id: e.id, name: personName(e), profilePicture: e.profilePicture }));
  }
  const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
  const withPath = departmentPath && targetType === TARGET.DEPARTMENT;
  if (withPath) params.includeHierarchy = true;
  const res = await api.get(path, { params });
  return listItems(res)
    // Sanal "Tüm Çalışanlar" grubu kural hedefi olamaz (sunucu policyRule.allEmployeesGroupNotAllowed);
    // herkes için "Tüm Şirket" hedefi kullanılır.
    .filter((x) => !(targetType === TARGET.USER_GROUP && x.id === ALL_EMPLOYEES_GROUP_ID))
    .map((x) => ({ id: x.id, name: (withPath && x.path) || x.name }));
};

const getTargetName = async (targetType, id, { departmentPath = false } = {}) => {
  const path = TARGET_SEARCH_PATHS[targetType];
  if (!path || !id) return { id, name: '' };
  if (targetType === TARGET.SUB_TENANT) {
    // Alt şirket ucunun tek kayıt okuması yok — listeden bulunur (employees/config deseni).
    const res = await api.get(path, { params: { pageSize: 100 } });
    const st = listItems(res).find((x) => x.id === id);
    return { id, name: st?.name || '' };
  }
  const res = await api.get(`${path}/${id}`);
  const d = res?.data?.data;
  if (targetType === TARGET.PERSON) return { id, name: personName(d) };
  if (departmentPath && targetType === TARGET.DEPARTMENT) return { id, name: d?.path || d?.name || '' };
  return { id, name: d?.name || '' };
};

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

// "yyyy-MM-dd" (DateOnly) YEREL gün olarak biçimlenir — new Date('yyyy-MM-dd') UTC gece yarısıdır ve batı
// saat dilimlerinde bir gün geri kayardı.
const formatDateOnly = (value, lang) => {
  if (!value) return '-';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '-';
  return formatDate(new Date(y, m - 1, d), lang);
};

export const EMPTY_RULE_FORM = Object.freeze({
  targetType: null, targetId: null, priority: '100', effectiveFrom: '', note: '',
});

const parsePriority = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return NaN;
  const n = Number(raw);
  return Number.isInteger(n) ? n : NaN;
};

export const ruleFormErrors = (form) => {
  const errors = {};
  if (form.targetType === null || form.targetType === undefined || form.targetType === '') errors.targetType = true;
  else if (Number(form.targetType) !== TARGET.COMPANY && !form.targetId) errors.targetId = true;
  const p = parsePriority(form.priority);
  if (Number.isNaN(p) || p < 1 || p > 9999) errors.priority = true;
  if (!form.effectiveFrom) errors.effectiveFrom = true;
  return errors;
};

// Sunucu gövdesi (PolicyRuleRequest). "Tüm Şirket" hedefinde targetId GÖNDERİLMEZ (sunucu dolu
// targetId'yi policyRule.invalidTargetType ile reddeder).
export const buildRuleBody = (kind, policyId, form) => {
  const targetType = Number(form.targetType);
  const body = {
    kind,
    policyId,
    targetType,
    priority: parsePriority(form.priority),
    effectiveFrom: form.effectiveFrom,
    note: form.note && form.note.trim() ? form.note.trim() : null,
  };
  if (targetType !== TARGET.COMPANY) body.targetId = form.targetId;
  return body;
};

/* ── Styled ── */

// Tek kaydırma: panel kabı ebeveynin kalan yüksekliğini alır ve küçülebilir (min-height:0); taşma yok —
// kayan yalnız DataTable satır alanıdır (yüksekliği aşağıda ölçülür).
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ErrorNote = styled.div`
  background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
  border-radius: 6px; padding: 10px 12px; font-size: 13px; margin: 0 0 12px; line-height: 1.4;
  flex-shrink: 0;
`;

// Eski atama uyarısı — ErrorNote ile aynı satır içi uyarı dili; sağda (yazma yetkisiyle) toplu kaldırma.
const LegacyNote = styled(ErrorNote)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
`;

const LegacyText = styled.span`
  min-width: 0;
`;

// Tablo alanı: uyarılardan KALAN yükseklik (flex:1 + min-height:0). Ölçülen bu alandır — üstteki
// uyarıların (yüklenemedi / eski atama) yüksekliği kendiliğinden düşülür, tek kaydırma bozulmaz.
const TableArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Hint = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #A0A8B4;
  margin-top: 4px;
  line-height: 1.45;
`;

const ResultBox = styled.div`
  background: #F1F5F9;
  border-radius: 6px;
  padding: 12px 14px;
  font-family: 'Nunito Sans', sans-serif;
`;

const ResultLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
`;

const ResultValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ResultSource = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
`;

// `all: true` = politikanın TÜM eski atamalarını kaldırma onayı (satır yok).
const EMPTY_CONFIRM = { isOpen: false, row: null, all: false };

export default function PolicyRulesPanel({
  kind, policyId, title, canWrite = false, tableBodyMaxHeight,
  targetTypes, departmentPathLabels = false, impactPreview, onChanged,
}) {
  const { t, i18n } = useTranslation();
  const { runWithPreview, dialog: impactDialog } = useImpactPreview();
  const lang = i18n?.language || 'tr';

  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 10 });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE_FORM);
  const [submitted, setSubmitted] = useState(false);
  const nameCache = useRef(new Map());

  const [confirm, setConfirm] = useState(EMPTY_CONFIRM);
  const [removing, setRemoving] = useState(false);

  const hasLegacyKind = LEGACY_KINDS.includes(kind);
  const [legacyCount, setLegacyCount] = useState(0);

  const [whoOpen, setWhoOpen] = useState(false);
  const [whoEmployeeId, setWhoEmployeeId] = useState(null);
  const [whoDate, setWhoDate] = useState('');
  const [whoResult, setWhoResult] = useState(null);

  // ── Tek kaydırma: tablo alanının yüksekliğinden tablo gövdesi yüksekliği (araç çubuğu + başlık + sayfalama
  // payı). Ölçülen tablo alanıdır (TableArea), panel değil — üstteki uyarılar zaten düşülmüş olur.
  const tableAreaRef = useRef(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = tableAreaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const bodyMaxHeight = tableBodyMaxHeight
    || (height ? `${Math.max(160, Math.floor(height - 160))}px` : undefined);

  // ── Liste (api.js GET'te reddetmez: sinyal res.data.status; .catch ölü kod olurdu).
  useEffect(() => {
    if (!kind || !policyId) return;
    let iptal = false;
    setFetching(true);
    policyRuleService.list(kind, policyId, queryParams.pageNumber || 1, queryParams.pageSize || 10)
      .then((res) => {
        if (iptal) return;
        if (res?.data?.status !== true) {
          setLoadFailed(true);
          setRows([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: queryParams.pageSize || 10 });
          return;
        }
        setLoadFailed(false);
        const result = res.data.data;
        const items = listItems(res);
        setRows(items);
        setPagination({
          totalRowCount: result?.totalRowCount ?? items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || queryParams.pageSize || 10,
        });
      })
      .finally(() => { if (!iptal) setFetching(false); });
    return () => { iptal = true; };
  }, [kind, policyId, queryParams, refreshTrigger]);

  // ── Eski atama sayısı (yalnız LEGACY_KINDS). Liste ile AYNI tetikle tazelenir: tekil "Atamayı Kaldır" ve
  // toplu kaldırma sonrası sayı da düşer. Uç başarısızsa (status !== true) uyarı gösterilmez.
  useEffect(() => {
    if (!hasLegacyKind || !policyId) {
      setLegacyCount(0);
      return undefined;
    }
    let iptal = false;
    Promise.resolve(policyRuleService.legacyCount(kind, policyId)).then((res) => {
      if (iptal) return;
      setLegacyCount(res?.data?.status === true ? (Number(res.data.data?.count) || 0) : 0);
    });
    return () => { iptal = true; };
  }, [hasLegacyKind, kind, policyId, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);
  const changed = useCallback(() => {
    refresh();
    if (onChanged) onChanged();
  }, [refresh, onChanged]);

  // ── Kural çekmecesi
  const openNew = useCallback(() => {
    setEditingRow(null);
    setForm({ ...EMPTY_RULE_FORM, effectiveFrom: todayStr() });
    setSubmitted(false);
    setDrawerOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    if (row.targetId && row.targetName) nameCache.current.set(String(row.targetId), row.targetName);
    setEditingRow(row);
    setForm({
      targetType: row.targetType,
      targetId: row.targetId ?? null,
      priority: String(row.priority ?? 100),
      effectiveFrom: row.effectiveFrom ? String(row.effectiveFrom).slice(0, 10) : '',
      note: row.note || '',
    });
    setSubmitted(false);
    setDrawerOpen(true);
  }, []);

  const setField = useCallback((name, value) => setForm((f) => ({ ...f, [name]: value })), []);

  // Hedef türü değişince önceki hedef TEMİZLENİR — "departman = <statü kimliği>" gibi kaydedilemeyen
  // bir seçim kalmasın.
  const changeTargetType = useCallback((value) => {
    setForm((f) => ({ ...f, targetType: value === null || value === undefined ? null : Number(value), targetId: null }));
  }, []);

  const errors = submitted ? ruleFormErrors(form) : {};

  const handleSave = useCallback(async () => {
    setSubmitted(true);
    if (Object.keys(ruleFormErrors(form)).length > 0) return false;
    const body = buildRuleBody(kind, policyId, form);
    const save = async () => {
      if (editingRow?.id) await policyRuleService.update(editingRow.id, body);
      else await policyRuleService.create(body);
    };
    if (impactPreview) {
      // Önizleme kimseyi değiştirmiyorsa kayıt hemen yapılır; değiştiriyorsa liste onaylanınca. Çekmece ancak kayıt
      // gerçekleşince kapanır.
      try {
        await runWithPreview(
          () => impactPreview(editingRow?.id
            ? { action: 'update', ruleId: editingRow.id, body }
            : { action: 'create', body }),
          async () => {
            await save();
            setDrawerOpen(false);
            changed();
          },
        );
      } catch {
        // interceptor toast'u; çekmece açık kalır
      }
      return false;
    }
    try {
      await save();
    } catch {
      // Hata toast'u interceptor'da (ör. policyRule.duplicate); çekmece AÇIK kalır.
      return false;
    }
    changed();
    return true;
  }, [form, kind, policyId, editingRow, changed, impactPreview, runWithPreview]);

  const targetType = form.targetType;
  const searchForType = useCallback(
    (q) => searchTargets(targetType, q, { departmentPath: departmentPathLabels }),
    [targetType, departmentPathLabels],
  );
  const getByIdForType = useCallback(async (id) => {
    const cached = nameCache.current.get(String(id));
    if (cached) return { id, name: cached };
    return getTargetName(targetType, id, { departmentPath: departmentPathLabels });
  }, [targetType, departmentPathLabels]);

  const targetTypeOptions = useMemo(() => (targetTypes || TARGET_TYPE_ORDER).map((tt) => ({
    id: tt, name: t(`policyRule.targetTypes.${TARGET_TYPE_KEYS[tt]}`),
  })), [t, targetTypes]);

  // ── Silme / eski atamayı kaldırma / tüm eski atamaları kaldırma
  const askRemove = useCallback((row) => setConfirm({ isOpen: true, row, all: false }), []);
  const askRemoveAllLegacy = useCallback(() => setConfirm({ isOpen: true, row: null, all: true }), []);

  const handleConfirm = useCallback(async () => {
    const { row, all } = confirm;
    if (!row && !all) return;
    if (impactPreview && row && !row.isLegacy && !all) {
      // Kural silmenin etkisi: onay penceresi kapanır, kimse değişmiyorsa silinir; değişiyorsa liste gösterilir.
      setConfirm(EMPTY_CONFIRM);
      try {
        await runWithPreview(
          () => impactPreview({ action: 'delete', ruleId: row.id }),
          async () => {
            await policyRuleService.remove(row.id);
            changed();
          },
        );
      } catch {
        // interceptor toast'u
      }
      return;
    }
    setRemoving(true);
    try {
      if (all) await policyRuleService.removeAllLegacy(kind, policyId);
      else if (row.isLegacy) await policyRuleService.removeLegacy(kind, row.targetId);
      else await policyRuleService.remove(row.id);
    } catch {
      // interceptor toast'u; pencere açık kalır, tablo yenilenmez.
      setRemoving(false);
      return;
    }
    setRemoving(false);
    setConfirm(EMPTY_CONFIRM);
    changed();
  }, [confirm, kind, policyId, changed, impactPreview, runWithPreview]);

  // ── Kim hangi kuralı alıyor?
  const openWho = useCallback(() => {
    setWhoEmployeeId(null);
    setWhoDate(todayStr());
    setWhoResult(null);
    setWhoOpen(true);
  }, []);

  const searchPeople = useCallback((q) => searchTargets(TARGET.PERSON, q), []);
  const getPerson = useCallback((id) => getTargetName(TARGET.PERSON, id), []);

  const handleWhoQuery = useCallback(async () => {
    if (!whoEmployeeId) return false;
    const res = await policyRuleService.effective(kind, whoEmployeeId, whoDate || undefined);
    setWhoResult(res?.data?.status === true ? (res.data.data || null) : null);
    // Sorgu çekmeceyi KAPATMAZ: sonuç çekmecenin içinde okunur.
    return false;
  }, [kind, whoEmployeeId, whoDate]);

  // ── Tablo
  const actionOptions = useMemo(() => {
    if (!canWrite) return [];
    // Eski atama satırı DÜZENLENMEZ (tarihsiz, kişi başı kayıttır) — yalnız "Atamayı Kaldır".
    // Tek silme kalemi; etiketi satıra göre (OptionsMenu `label(row)` sözleşmesi, mükerrer key yok).
    return [
      { type: 'edit', label: t('policyRule.edit'), onClick: openEdit, condition: (row) => !row?.isLegacy },
      {
        type: 'delete',
        label: (row) => (row?.isLegacy ? t('policyRule.removeLegacy') : t('policyRule.delete')),
        onClick: askRemove,
      },
    ];
  }, [canWrite, t, openEdit, askRemove]);

  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'targetType',
      label: t('policyRule.targetType'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          if (row?.isLegacy) return t('policyRule.legacy');
          const key = TARGET_TYPE_KEYS[v];
          return key ? t(`policyRule.targetTypes.${key}`) : '-';
        },
      },
    },
    {
      name: 'targetName',
      label: t('policyRule.target'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => {
          const row = rows[meta.rowIndex];
          if (row && !row.isLegacy && row.targetType === TARGET.COMPANY) return t('policyRule.targetTypes.company');
          return v || '-';
        },
      },
    },
    {
      name: 'priority',
      label: t('policyRule.priority'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => (rows[meta.rowIndex]?.isLegacy ? '-' : (v ?? '-')),
      },
    },
    {
      name: 'effectiveFrom',
      label: t('policyRule.effectiveFrom'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => (rows[meta.rowIndex]?.isLegacy || !v
          ? t('policyRule.fromBeginning')
          : formatDateOnly(v, lang)),
      },
    },
    { name: 'note', label: t('policyRule.note'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
  ], [t, rows, lang]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, meta) => <OptionsMenu actions={actionOptions} row={rows[meta.rowIndex]} />,
        },
      },
    ];
  }, [baseColumns, actionOptions, rows]);

  const handleTableChanged = useCallback((action, tableState) => {
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams({ pageNumber: params.pageNumber, pageSize: params.pageSize });
  }, [baseColumns]);

  const confirmRow = confirm.row;
  const confirmTitle = confirm.all
    ? t('policyRule.removeAllLegacy')
    : (confirmRow?.isLegacy ? t('policyRule.removeLegacy') : t('policyRule.delete'));
  let confirmMessage = t('policyRule.deleteMessage');
  if (confirm.all) confirmMessage = t('policyRule.removeAllLegacyConfirm', { count: legacyCount });
  else if (confirmRow?.isLegacy) confirmMessage = t('policyRule.removeLegacyMessage', { name: confirmRow?.targetName || '-' });

  return (
    <Wrapper data-testid="policy-rules-panel">
      {loadFailed && <ErrorNote role="alert" data-testid="policy-rules-load-failed">{t('policyRule.loadFailed')}</ErrorNote>}

      {hasLegacyKind && legacyCount > 0 && (
        <LegacyNote role="status" data-testid="policy-rules-legacy-notice">
          <LegacyText>{t('policyRule.legacyNotice', { count: legacyCount })}</LegacyText>
          {canWrite && (
            <Button variant="outline" onClick={askRemoveAllLegacy} data-testid="policy-rules-remove-all-legacy">
              {t('policyRule.removeAllLegacy')}
            </Button>
          )}
        </LegacyNote>
      )}

      <TableArea ref={tableAreaRef}>
        <DataTable
          title={title || t('policyRule.title')}
          fetching={fetching}
          columns={columns}
          data={rows}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={!!canWrite}
          newButtonLabel={t('policyRule.new')}
          onNewButtonClick={openNew}
          toolbarActions={(
            <Button variant="outline" onClick={openWho} data-testid="policy-rules-who">
              {t('policyRule.whoGetsWhat')}
            </Button>
          )}
          onTableChanged={handleTableChanged}
          disableFilters
          tableBodyMaxHeight={bodyMaxHeight}
        />
      </TableArea>

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingRow ? t('policyRule.edit') : t('policyRule.new')}
        schema={[]}
        onSave={handleSave}
      >
        <Field data-testid="policy-rule-target-type">
          <GenericSelect
            label={t('policyRule.targetType')}
            options={targetTypeOptions}
            value={form.targetType}
            onChange={changeTargetType}
            idKey="id"
            labelKey="name"
            required
            error={errors.targetType ? t('common.requiredField') : undefined}
          />
          <Hint>{t('policyRule.precedenceHint')}</Hint>
        </Field>

        {targetType !== null && targetType !== TARGET.COMPANY && (
          <Field data-testid="policy-rule-target">
            <AsyncGenericSelect
              key={`target-${targetType}`}
              label={t('policyRule.target')}
              value={form.targetId}
              onChange={(v) => setField('targetId', v || null)}
              onSearch={searchForType}
              onGetById={getByIdForType}
              renderOption={targetType === TARGET.PERSON
                ? (o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />
                : undefined}
              required
              error={errors.targetId ? t('common.requiredField') : undefined}
            />
          </Field>
        )}

        <Field>
          <TextInput
            label={t('policyRule.priority')}
            type="number"
            min={1}
            max={9999}
            value={form.priority}
            onChange={(e) => setField('priority', e.target.value)}
            required
            error={errors.priority ? t('policyRule.priorityRange') : undefined}
          />
          <Hint>{t('policyRule.priorityHint')}</Hint>
        </Field>

        <Field>
          <DateTimePicker
            label={t('policyRule.effectiveFrom')}
            mode="date"
            value={form.effectiveFrom}
            onChange={(v) => setField('effectiveFrom', v || '')}
            required
            error={errors.effectiveFrom ? t('common.requiredField') : undefined}
          />
        </Field>

        <Field>
          <TextInput
            label={t('policyRule.note')}
            type="textarea"
            rows={3}
            value={form.note}
            onChange={(e) => setField('note', e.target.value)}
          />
        </Field>
      </GenericFormDrawer>

      <GenericFormDrawer
        open={whoOpen}
        onClose={() => setWhoOpen(false)}
        title={t('policyRule.whoGetsWhat')}
        schema={[]}
        onSave={handleWhoQuery}
        submitLabel={t('policyRule.check')}
        submitDisabled={!whoEmployeeId}
      >
        <Field>
          <AsyncGenericSelect
            label={t('policyRule.targetTypes.person')}
            value={whoEmployeeId}
            onChange={(v) => { setWhoEmployeeId(v || null); setWhoResult(null); }}
            onSearch={searchPeople}
            onGetById={getPerson}
            renderOption={(o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />}
            required
          />
        </Field>
        <Field>
          <DateTimePicker
            label={t('policyRule.date')}
            mode="date"
            value={whoDate}
            onChange={(v) => { setWhoDate(v || ''); setWhoResult(null); }}
          />
        </Field>
        {whoResult && (
          <ResultBox data-testid="policy-rule-effective-result">
            <ResultLabel>{t('policyRule.effectiveResult')}</ResultLabel>
            <ResultValue>{whoResult.policyName || t('policyRule.source.none')}</ResultValue>
            <ResultSource>
              {t(`policyRule.source.${whoResult.source || 'none'}`)}
              {whoResult.targetName ? ` — ${whoResult.targetName}` : ''}
            </ResultSource>
          </ResultBox>
        )}
      </GenericFormDrawer>

      {impactDialog}

      <ConfirmDialog
        isOpen={confirm.isOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(EMPTY_CONFIRM)}
        loading={removing}
      />
    </Wrapper>
  );
}
